import React, { useCallback, useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@material-ui/core";
import { getFieldProps, inputProps } from "../../utils";
import MultiSelect from "../../shared/MultiSelect";
import { useModals } from "../../../hooks/useModals";
import { useAppContext } from "../../../api/AppContext";

/**
 * Condition editor for HR documents — same attribute model as welfare Requirements.
 */
const HrDocumentConditions = ({ formik, welfareSelectors }) => {
  const [isAngeliniUsersLoading, setIsAngeliniUsersLoading] = useState(false);
  const modals = useModals();
  const { fetchAngeliniUsers } = useAppContext();

  const handleChange = useCallback(
    (value, field) => {
      formik.setFieldValue(field, value);
      formik.setFieldTouched(field, true);
    },
    [formik]
  );

  const multiselector = (name, options, label) => {
    const items = (options || []).map((option) => ({
      id: option.id,
      name: option.displayname,
    }));

    return (
      <MultiSelect
        {...getFieldProps(formik, { name })}
        items={items}
        label={label}
        onChange={(value) => handleChange(value, name)}
      />
    );
  };

  const handleSpecificUsers = useCallback(async () => {
    const dataObject = {};
    const buildUsersWithFallback = (requestedIds = [], fetchedUsers = []) => {
      const fetchedUsersById = new Map(
        fetchedUsers.map((user) => [String(user.id), user])
      );
      return requestedIds.map(
        (id) =>
          fetchedUsersById.get(String(id)) || {
            givenname: "another company",
            id,
            mail: "details not visible",
            surname: "Record of",
          }
      );
    };

    if (formik.values.conditions.forUserIds?.length) {
      dataObject.enabledUsers = formik.values.conditions.forUserIds;
    }
    if (formik.values.conditions.notForUserIds?.length) {
      dataObject.disabledUsers = formik.values.conditions.notForUserIds;
    }

    let results;
    if (dataObject.enabledUsers?.length || dataObject.disabledUsers?.length) {
      setIsAngeliniUsersLoading(true);
      results = await fetchAngeliniUsers(dataObject);
      setIsAngeliniUsersLoading(false);
    }

    modals.openModal(modals.modalTypes.SpecificUsers, {
      disabledUsers: buildUsersWithFallback(
        dataObject.disabledUsers,
        results?.disabledUsers
      ),
      enabledUsers: buildUsersWithFallback(
        dataObject.enabledUsers,
        results?.enabledUsers
      ),
      onConfirm: async ({ enabledUsers, disabledUsers }) => {
        formik.setFieldValue(
          "conditions.forUserIds",
          enabledUsers.map((user) => user.id)
        );
        formik.setFieldValue(
          "conditions.notForUserIds",
          disabledUsers.map((user) => user.id)
        );
      },
    });
  }, [formik, modals, fetchAngeliniUsers]);

  return (
    <Grid item xs={12}>
      <Typography variant="h6">Audience (who can use this document)</Typography>
      <Typography color="textSecondary" variant="body2">
        Same attribute model as Employee Registry / welfare conditions. Users in
        &quot;not for&quot; are always excluded.
      </Typography>
      <Grid container spacing={2} style={{ marginTop: "0.5rem" }}>
        <Grid item md={6} xs={12}>
          <TextField
            fullWidth
            required
            select
            InputLabelProps={{ shrink: true }}
            InputProps={inputProps}
            label="Entity"
            margin="none"
            variant="outlined"
            {...getFieldProps(formik, { name: "conditions.entity" })}
          >
            {(welfareSelectors.entities || []).map((elem) => (
              <MenuItem key={elem.id} value={elem.id}>
                {elem.displayname}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item md={6} xs={12}>
          {multiselector(
            "conditions.officeTypes",
            welfareSelectors.officeType,
            "Office field"
          )}
        </Grid>
        <Grid item md={6} xs={12}>
          {multiselector(
            "conditions.contractTypes",
            welfareSelectors.contractType,
            "Contract type"
          )}
        </Grid>
        <Grid item md={6} xs={12}>
          {multiselector(
            "conditions.employeeTypes",
            welfareSelectors.employeeType,
            "Employee type"
          )}
        </Grid>
        <Grid item md={6} xs={12}>
          {multiselector(
            "conditions.employeeRoles",
            welfareSelectors.employeeRole,
            "Role"
          )}
        </Grid>
        <Grid item md={6} xs={12}>
          {multiselector(
            "conditions.officeLocations",
            welfareSelectors.officeLocation,
            "Office location"
          )}
        </Grid>
      </Grid>
      <Grid item style={{ marginTop: "1rem" }}>
        <Typography color="textSecondary" variant="body2">
          Enable or disable specific people?
          {isAngeliniUsersLoading ? (
            <CircularProgress size={25} style={{ marginLeft: "1rem" }} />
          ) : (
            <Button color="primary" onClick={handleSpecificUsers}>
              Click here
            </Button>
          )}
        </Typography>
      </Grid>
    </Grid>
  );
};

export default HrDocumentConditions;
